import { ordinaryProfile, SpeechSegmenter } from './speechSegmenter'
import { UtteranceFeed } from './utteranceFeed'
import { NoiseFloorDetector } from './voiceDetectors'

/** Audio block size analyzed by the detector, in samples (~85 ms at 48 kHz) */
const BLOCK_SIZE = 4096

/**
 * Detects utterances in an audio stream for ordinary transcription:
 * the browser microphone, or the mixed audio of the capture app.
 * It listens to the stream in its own audio graph, next to the WebRTC connection that sends the same stream to OpenAI.
 * So the commits need no delay, unlike the speaker channels (see `UtteranceFeed.flush`).
 *
 * Start it with the audio, before the OpenAI connection is ready, so it can learn the background noise.
 * The analysis runs in a ScriptProcessorNode callback on the main thread.
 * Unlike timers, these callbacks aren't throttled in background tabs.
 */
export class StreamSpeechDetection {
  private readonly audioContext = new AudioContext()
  /** Utterances of the stream, for its OpenAI session. */
  readonly utterances: UtteranceFeed

  constructor(stream: MediaStream) {
    const { audioContext } = this
    // Analyze each callback as one block (about 85 ms), as the detector was tuned for.
    const segmenter = new SpeechSegmenter(
      [new NoiseFloorDetector(audioContext.sampleRate)],
      ordinaryProfile,
      audioContext.sampleRate,
    )
    let analyzedFrames = 0
    this.utterances = new UtteranceFeed(segmenter, 0, () => analyzedFrames)
    const processor = audioContext.createScriptProcessor(BLOCK_SIZE, 1, 1)
    processor.onaudioprocess = event => {
      const samples = event.inputBuffer.getChannelData(0)
      this.utterances.accept(segmenter.process([samples]))
      analyzedFrames += samples.length
      this.utterances.flush()
    }
    audioContext.createMediaStreamSource(stream).connect(processor)
    // The processor only runs when connected to the output (it outputs silence)
    processor.connect(audioContext.destination)
    void audioContext.resume()
  }

  /** Stop the analysis. The stream itself keeps running. */
  stop() {
    void this.audioContext.close()
  }
}
